import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import dynamic from 'next/dynamic';

import withI18n from '../components/withI18n';
import Page from '../containers/Page';
import PageFooter from '../containers/PageFooter';
import i18n from '../utils/i18n';
import AnalyticsTrackers from '../containers/AnalyticsTrackers';
import OrderStatusContainer from '../containers/OrderStatus';
import OrderStatusSkeletonContainer from '../containers/OrderStatusSkeleton';
import OrderHelpModalContainer from '../containers/OrderHelpModal';
import ChatWebClient from '../containers/ChatWebClient';
import { deriveCustomerOrderId, statusForState } from '../utils/pages/order-status';
import { urlWithLocale } from '../utils/locale';

import {
  setHelpOrderId,
  getOrderStatus,
  getOrderStatusFromLocalStorage,
  getOrderStatusFailedAttemptsFromLocalStorage,
  setOrderStatusFailedAttemptsFromLocalStorage,
  getOrderStatusTrackingData,
  setOrderStatusShared,
  getOrderPunchCard,
} from '../actions/orderActions';
import {
  trackViewedOrderStatusPage,
  trackViewedWhatsappBanner,
} from '../actions/tracking/orderStatus';
import { trackViewedSharedOrderPage } from '../actions/tracking/orderStatusSharing';
import { requestConfig } from '../actions/configActions';
import { isFeatureActive } from '../selectors/configSelectors';
import { CX_SHAREABLE_ORDER_STATUS } from '../flags/features';
import sentry from '../utils/sentry';
import CustomErrorPage from './_error';

const { Sentry, captureException } = sentry();

class OrderStatus extends Component {
  static async getPageData(context, dispatch, getState) {
    const { res, query } = context;
    const orderId = query.orderId;
    const isSharedOrder = Boolean(query.sharing_token);

    dispatch(setOrderStatusShared(isSharedOrder));

    await Promise.all([
      dispatch(requestConfig()),
      dispatch(setHelpOrderId(orderId)),
      dispatch(getOrderStatus(orderId)),
      dispatch(getOrderStatusTrackingData(orderId, isSharedOrder)),
    ]);
    // since this depends on requestConfig to have fetched the features list
    await dispatch(getOrderPunchCard(orderId));

    const state = getState();
    const { status, error } = statusForState(state);
    const orderStatus = state.order ? state.order.status : null;
    const { country, locale } = state.request;

    if (res && status) {
      res.statusCode = status;

      if (error) {
        Sentry.withScope((scope) => {
          scope.setExtras({
            orderStatusApiStatusCode: status,
            'order.fulfillmentType': orderStatus.fulfillmentType,
          });
          captureException(error);
        });
      }
    }

    if (state.user && !state.user.isLoggedIn && !isSharedOrder) {
      res.redirect(301, `/login?redirect=${encodeURI(`/orders/${orderId}/status`)}`);
    }

    /** if the order is a shared order and the feature is off */
    if (isSharedOrder && !isFeatureActive(state, CX_SHAREABLE_ORDER_STATUS)) {
      res.redirect(301, '/');
    }

    if (status === 401) {
      res.redirect(301, '/');
    }

    if (
      !isSharedOrder &&
      orderStatus &&
      (orderStatus.isFailedStatus || orderStatus.isCompletedStatus)
    ) {
      if (res && country && locale) {
        res.redirect(301, urlWithLocale(country, `/orders/${orderStatus.order.id}`, locale));
      } else {
        res.redirect(301, `/orders/${orderStatus.order.id}`);
      }
    }

    if (!isSharedOrder) {
      dispatch(trackViewedOrderStatusPage(true));
    }

    dispatch(trackViewedWhatsappBanner());
  }

  componentDidMount() {
    this.props.getOrderStatusFromLocalStorage();
    this.props.getOrderStatusFailedAttemptsFromLocalStorage();

    if (this.props.isSharedOrder) {
      this.props.onViewedSharedOrderPage();
    }
  }

  componentDidUpdate(prevProps) {
    const { orderId, failedAttempts } = this.props;
    if (prevProps.failedAttempts !== failedAttempts) {
      setOrderStatusFailedAttemptsFromLocalStorage(orderId, failedAttempts);
    }
  }

  render() {
    const { statusCode, tracking, orderId, isLoadingOrderStatus, isInviteModalOpen } = this.props;

    const pageTitle = i18n.gettext('order_status_page.page_title', {
      orderId: deriveCustomerOrderId(orderId),
    });

    if (statusCode !== 200) {
      return <CustomErrorPage statusCode={statusCode} />;
    }

    let InviteModalContainer = () => null;

    if (isInviteModalOpen) {
      InviteModalContainer = dynamic(import('../containers/InviteModalContainer'), {
        ssr: true,
      });
    }

    return (
      <Page
        color="anchovy"
        pageName="order-status"
        pageTitle={pageTitle}
        pagePath={`/orders/${orderId}/status`}
      >
        <ChatWebClient orderId={orderId} />
        {isLoadingOrderStatus ? <OrderStatusSkeletonContainer /> : <OrderStatusContainer />}
        <OrderHelpModalContainer />
        <InviteModalContainer />
        <PageFooter />
        <AnalyticsTrackers orderStatusTracking={tracking} orderId={orderId} />
      </Page>
    );
  }
}

OrderStatus.propTypes = {
  isLoadingOrderStatus: PropTypes.bool.isRequired,
  statusCode: PropTypes.number.isRequired,
  tracking: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  orderId: PropTypes.string.isRequired,
  failedAttempts: PropTypes.number,
  getOrderStatusFromLocalStorage: PropTypes.func.isRequired,
  getOrderStatusFailedAttemptsFromLocalStorage: PropTypes.func,
  onViewedSharedOrderPage: PropTypes.func.isRequired,
  isSharedOrder: PropTypes.bool,
  isInviteModalOpen: PropTypes.bool.isRequired,
};

OrderStatus.defaultProps = {
  tracking: null,
  isSharedOrder: false,
  failedAttempts: 0,
  getOrderStatusFailedAttemptsFromLocalStorage: () => {},
};

function mapStateToProps(state) {
  const { error, tracking, order, attributes, isSharedOrder, failedAttempts } = state.order.status;
  const { status } = statusForState(state);
  const orderStatusStatusCode = error && error.statusCode;
  const isPendingStatus = attributes && attributes.uiStatus && attributes.uiStatus === 'PENDING';
  const isLoadingOrderStatus = Boolean((orderStatusStatusCode && !order) || isPendingStatus);

  return {
    isLoadingOrderStatus,
    statusCode: status,
    tracking,
    orderId: state.request.currentQuery.orderId,
    isSharedOrder,
    isInviteModalOpen: state.user.roovite.showModal,
    failedAttempts,
  };
}

const mapActionsToProps = (dispatch) => ({
  getOrderStatusFromLocalStorage: () => dispatch(getOrderStatusFromLocalStorage()),
  getOrderStatusFailedAttemptsFromLocalStorage: () =>
    dispatch(getOrderStatusFailedAttemptsFromLocalStorage()),
  onViewedSharedOrderPage: () => dispatch(trackViewedSharedOrderPage()),
});

export default connect(
  mapStateToProps,
  mapActionsToProps,
)(withI18n(OrderStatus));
